import React from 'react';
import { View, Text, Image, StyleSheet, Platform, Pressable, useWindowDimensions } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { LinearGradient } from 'expo-linear-gradient';
import { Typography, Button } from './ui';
import { useTheme } from '../context/ThemeContext';
import { fonts } from '../styles/typography';
import { useI18n } from '../i18n';

import { ACCENT_TEXT } from '../styles/accentText';
import { surfaceFill } from '../styles/platformBackgrounds';
export type AuthCTATrigger = 'onboarding' | 'gated_action';

interface AuthCTAModalProps {
  headingOverride?: string;
  appearance?: 'rhythm';
  trigger: AuthCTATrigger;
  description: string | null;
  onSignUp: () => void;
  onLogIn: () => void;
  onDismiss: () => void;
}

const ICONS = {
  onboarding: 'sparkles' as const,
  gated_action: 'lock-closed-outline' as const,
};

export const AuthCTAModal: React.FC<AuthCTAModalProps> = ({
  trigger,
  headingOverride,
  appearance,
  description,
  onSignUp,
  onLogIn,
  onDismiss,
}) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= 768;
  const { colors: themeColors, isDark } = useTheme();
  const { t } = useI18n('common');

  const rhythm = appearance === 'rhythm' && Platform.OS === 'web';
  const ink = rhythm ? (isDark ? '#FFF4DF' : '#314C48') : themeColors.textPrimary;
  const icon = ICONS[trigger as keyof typeof ICONS] || ICONS.gated_action;
  const heading = headingOverride ?? (trigger === 'gated_action'
    ? t('authCta.gatedAction.heading')
    : t('authCta.onboarding.heading'));
  const bodyText = trigger === 'gated_action'
    ? (description || t('authCta.gatedAction.body'))
    : t('authCta.onboarding.body');

  return (
    <Pressable style={styles.backdrop} onPress={onDismiss}>
      <Pressable
        testID="auth-cta-card"
        // CORE-1733 (возврат QA) — окно — поверхность v4 (статья 110756133), не плоская .90.
        style={[styles.card, isDesktop && styles.cardDesktop, surfaceFill(isDark ? 'dark' : 'light'), rhythm && { overflow: 'hidden', borderWidth: 1, borderColor: isDark ? '#B58B68' : '#E6BD91' }]}
        onPress={(e) => e.stopPropagation()}
      >
        {rhythm && <LinearGradient
          colors={isDark ? ['#384D4A', '#303B3F', '#4C3A32'] : ['#FFF6D8', '#EFF4DF', '#FFE1C3']}
          start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }}
          style={StyleSheet.absoluteFill}
        />}
        {rhythm ? <Image
          source={require('../../assets/home/agapella-approved-flower.webp')}
          style={{ width: 80, height: 80, marginBottom: 24 }}
          resizeMode="contain" accessible={false}
        /> : <View style={styles.iconCircle}>
          <Ionicons name={icon} size={24} color={ACCENT_TEXT} />
        </View>}

        <Text accessibilityRole="header" style={[styles.heading, { color: ink }]}>{heading}</Text>
        <Typography variant="body" style={[styles.body, { color: ink, opacity: rhythm ? 0.85 : 0.7 }]}>{bodyText}</Typography>

        <View style={styles.buttons}>
          {rhythm ? <Pressable accessibilityRole="button" onPress={onSignUp} style={styles.rhythmPrimary}>
            <LinearGradient colors={['#FF9756', '#FFD3A6']} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={StyleSheet.absoluteFill} />
            <Text style={styles.rhythmPrimaryText}>{t('buttons.signUp')}</Text>
            <Ionicons name="arrow-forward" size={18} color="#352D24" />
          </Pressable> : <Button variant="convex-fire" onPress={onSignUp} style={styles.btn}>
            {t('buttons.signUp')}
          </Button>}
          <Button variant="secondary" onPress={onLogIn} style={[styles.btn, isDark && { borderColor: 'rgba(255,255,255,0.15)' }, rhythm && { borderColor: isDark ? '#C5A17B' : '#9C9C83', borderWidth: 1.5 }]}>
            {t('buttons.logIn')}
          </Button>
        </View>

        <Pressable onPress={onDismiss} style={styles.notNow}>
          <Typography variant="body" style={[styles.notNowText, { color: themeColors.textPrimary }]}>{t('buttons.notNow')}</Typography>
        </Pressable>
      </Pressable>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    position: Platform.OS === 'web' ? 'fixed' : 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 5000,
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(4px)',
    }),
  } as any,
  card: {
    borderRadius: 32,
    paddingHorizontal: 32,
    paddingTop: 40,
    paddingBottom: 32,
    width: '90%',
    maxWidth: 400,
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 24px 80px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.05)',
    }),
  } as any,
  cardDesktop: {
    paddingHorizontal: 48,
    paddingTop: 48,
    paddingBottom: 40,
  },
  iconCircle: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: 'rgba(233, 130, 69, 0.08)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  heading: {
    textAlign: 'center',
    marginBottom: 8,
    fontFamily: Platform.OS === 'web' ? fonts.brand : fonts.heading,
    fontWeight: 'normal',
    fontSize: 26,
    lineHeight: 34,
    letterSpacing: 0,
  },
  body: {
    textAlign: 'center',
    opacity: 0.7,
    marginBottom: 36,
    lineHeight: 24,
    fontSize: 16,
    maxWidth: 320,
    fontFamily: fonts.text,
    fontWeight: 'normal',
  },
  buttons: {
    width: '100%',
    gap: 12,
  },
  btn: {
    width: '100%',
    height: 52,
    borderRadius: 14,
  },
  rhythmPrimary: {
    minHeight: 52, borderRadius: 16, overflow: 'hidden', borderWidth: 1,
    borderColor: '#E9AD78', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10,
    paddingHorizontal: 12,
  },
  rhythmPrimaryText: { fontFamily: fonts.text, fontSize: 16, fontWeight: '700', color: '#352D24' },
  notNow: {
    marginTop: 20,
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  notNowText: {
    opacity: 0.5,
    fontWeight: '600',
    fontSize: 15,
  },
});
